import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getData } from '../services/api.service';

function OrderPublicView() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrderDetails();
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await getData(`/api/orders/get_detailed_orders?order_id=${orderId}`, false);
      
      if (response?.data?.orders && response.data.orders.length > 0) {
        const fullOrder = response.data.orders[0];
        
        // Calcular el total sumando todos los items
        const total = fullOrder.items?.reduce((sum, item) => {
          return sum + (item.quantity * item.sale_price);
        }, 0) || 0;
        
        fullOrder.total = total;
        setOrder(fullOrder);
      } else {
        setError('Pedido no encontrado');
      }
    } catch (err) {
      console.error('Error cargando pedido:', err);
      setError('Error al cargar el pedido');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      1: 'bg-warning text-dark',
      2: 'bg-info',
      3: 'bg-primary',
      4: 'bg-success',
      5: 'bg-danger'
    };
    return statusMap[status] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando pedido...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mt-5">
        <div className="alert alert-warning text-center">
          <i className="fas fa-info-circle me-2"></i>
          No se encontró el pedido
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4 mb-5">
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              <i className="fas fa-receipt me-2"></i>
              Pedido #{order.order_id}
            </h4>
            <span className={`badge ${getStatusBadgeClass(order.status)}`}>
              {order.status_description}
            </span>
          </div>
        </div>

        <div className="card-body">
          {/* Información del Cliente */}
          <div className="row mb-4">
            <div className="col-md-12">
              <h5 className="text-primary mb-3">
                <i className="fas fa-user me-2"></i>
                Información del Cliente
              </h5>
            </div>
            <div className="col-md-6">
              <div className="mb-2">
                <strong>Nombre:</strong> {order.client_name || 'Cliente directo'}
              </div>
              <div className="mb-2">
                <strong>DNI:</strong> {order.client_dni || 'N/A'}
              </div>
              <div className="mb-2">
                <strong>Teléfono:</strong> {order.client_cellphone || 'No disponible'}
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-2">
                <strong>Email:</strong> {order.customer_email || 'No disponible'}
              </div>
              <div className="mb-2">
                <strong>Dirección:</strong> {order.client_address || 'No disponible'}
              </div>
              <div className="mb-2">
                <strong>Provincia/Ciudad:</strong> {order.customer_province || 'N/A'} / {order.customer_city || 'N/A'}
              </div>
            </div>
          </div>

          <hr />

          {/* Productos */}
          <div className="row mb-4">
            <div className="col-md-12">
              <h5 className="text-primary mb-3">
                <i className="fas fa-shopping-cart me-2"></i>
                Productos
              </h5>
            </div>
            <div className="col-md-12">
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Producto</th>
                      <th>Material</th>
                      <th>Medidas</th>
                      <th className="text-center">Cantidad</th>
                      <th className="text-end">Precio</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items && order.items.length > 0 ? (
                      order.items.map((item, index) => (
                        <React.Fragment key={index}>
                          <tr>
                            <td>
                              <strong>{item.sku_description || `Producto ${index + 1}`}</strong>
                              <br />
                              <small className="text-muted">SKU: {item.sku || 'N/A'}</small>
                            </td>
                            <td>{item.details?.material_name || '-'}</td>
                            <td>{item.details?.measurements || '-'}</td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-end">{formatCurrency(item.sale_price)}</td>
                            <td className="text-end">
                              <strong>{formatCurrency(item.sale_price * item.quantity)}</strong>
                            </td>
                          </tr>
                          {item.details?.colors && item.details.colors.length > 0 && (
                            <tr>
                              <td colSpan="6" className="bg-light">
                                <small>
                                  <strong>Colores:</strong> {item.details.colors.map(c => c.color_name).join(', ')}
                                </small>
                              </td>
                            </tr>
                          )}
                          {item.details?.additional_accessories && item.details.additional_accessories.length > 0 && (
                            <tr>
                              <td colSpan="6" className="bg-light">
                                <small>
                                  <strong>Accesorios:</strong> {item.details.additional_accessories.map(a => a.accessory_name).join(', ')}
                                </small>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          No hay productos en este pedido
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <td colSpan="5" className="text-end">
                        <strong>TOTAL:</strong>
                      </td>
                      <td className="text-end">
                        <strong className="text-success fs-5">
                          {formatCurrency(order.total)}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <hr />

          {/* Información del Pedido */}
          <div className="row">
            <div className="col-md-6">
              <h5 className="text-primary mb-3">
                <i className="fas fa-info-circle me-2"></i>
                Detalles del Pedido
              </h5>
              <div className="mb-2">
                <strong>Fecha:</strong> {formatDate(order.inserted_date)}
              </div>
              <div className="mb-2">
                <strong>Vendedor:</strong> {order.seller_name}
              </div>
              <div className="mb-2">
                <strong>Tipo de entrega:</strong> {order.delivery_description || 'No especificado'}
              </div>
              <div className="mb-2">
                <strong>Medio de pago:</strong> {order.payment_description || 'No especificado'}
              </div>
            </div>
            <div className="col-md-6">
              <h5 className="text-primary mb-3">
                <i className="fas fa-dollar-sign me-2"></i>
                Estado de Pago
              </h5>
              <div className="mb-2">
                <strong>Acreditado:</strong>{' '}
                <span className="text-success">{formatCurrency(order.credited_payment)}</span>
              </div>
              <div className="mb-2">
                <strong>Saldo:</strong>{' '}
                <span className="text-info">{formatCurrency(order.credit_balance)}</span>
              </div>
            </div>
          </div>

          {order.observations && (
            <>
              <hr />
              <div className="row">
                <div className="col-md-12">
                  <h5 className="text-primary mb-3">
                    <i className="fas fa-comment me-2"></i>
                    Observaciones
                  </h5>
                  <p className="text-muted">{order.observations}</p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="card-footer text-center text-muted">
          <small>
            <i className="fas fa-clock me-1"></i>
            Vista generada el {new Date().toLocaleString('es-AR')}
          </small>
        </div>
      </div>
    </div>
  );
}

export default OrderPublicView;
